export type SlackWelcomeKind = "channel" | "community"

export interface SlackWelcomeVariable {
	key: "name" | "channel"
	description: string
}

const NAME_VARIABLE: SlackWelcomeVariable = {
	key: "name",
	description: "Their first name",
}
const CHANNEL_VARIABLE: SlackWelcomeVariable = {
	key: "channel",
	description: "Channel they joined",
}

export const SLACK_WELCOME_VARIABLES: Record<
	SlackWelcomeKind,
	SlackWelcomeVariable[]
> = {
	channel: [NAME_VARIABLE, CHANNEL_VARIABLE],
	community: [NAME_VARIABLE],
}

export const DEFAULT_SLACK_WELCOME_MESSAGES: Record<SlackWelcomeKind, string> =
	{
		channel:
			"Hi {name}, welcome to {channel}.\nAsk your questions here and our team will answer.",
		community:
			"Hi {name}, welcome to the community.\nPost a question in any channel and our team or another member will answer.",
	}

/** The stored message, or the default when the workspace never wrote one. */
export function slackWelcomeTemplate(
	kind: SlackWelcomeKind,
	stored: string | null | undefined,
): string {
	return stored?.trim() ? stored : DEFAULT_SLACK_WELCOME_MESSAGES[kind]
}

// Slack reads &, < and > as control characters in mrkdwn, so a display name
// like "<!channel>" would otherwise ping the whole channel.
export function escapeSlackText(text: string): string {
	return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

export function renderSlackWelcomeMessage({
	template,
	name,
	channelId,
}: {
	template: string
	name?: string | null
	channelId?: string | null
}): string {
	const trimmedName = name?.trim()
	return template
		.replace(/\{name\}/g, trimmedName ? escapeSlackText(trimmedName) : "there")
		.replace(/\{channel\}/g, channelId ? `<#${channelId}>` : "this channel")
}

export interface SlackPreviewSegment {
	text: string
	bold?: boolean
	italic?: boolean
	strike?: boolean
	code?: boolean
	link?: boolean
	mention?: boolean
}

const unescapeSlackText = (text: string) =>
	text.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&")

function angleSegment(inner: string): SlackPreviewSegment {
	const [target = "", label] = inner.split("|")
	if (target.startsWith("#"))
		return { text: `#${label ?? target.slice(1)}`, mention: true }
	if (target.startsWith("@") || target.startsWith("!"))
		return { text: `@${label ?? target.slice(1)}`, mention: true }
	return { text: unescapeSlackText(label ?? target), link: true }
}

const MRKDWN_TOKEN =
	/\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~|`([^`\n]+)`|<([^>\n]+)>/g

/** Splits Slack mrkdwn into styled runs per line, for a preview that reads like Slack. */
export function parseSlackMrkdwn(text: string): SlackPreviewSegment[][] {
	return text.split("\n").map((line) => {
		const segments: SlackPreviewSegment[] = []
		let last = 0
		for (const match of line.matchAll(MRKDWN_TOKEN)) {
			const index = match.index ?? 0
			if (index > last)
				segments.push({ text: unescapeSlackText(line.slice(last, index)) })
			const [, bold, italic, strike, code, angle] = match
			if (bold !== undefined)
				segments.push({ text: unescapeSlackText(bold), bold: true })
			else if (italic !== undefined)
				segments.push({ text: unescapeSlackText(italic), italic: true })
			else if (strike !== undefined)
				segments.push({ text: unescapeSlackText(strike), strike: true })
			else if (code !== undefined) segments.push({ text: code, code: true })
			else if (angle !== undefined) segments.push(angleSegment(angle))
			last = index + match[0].length
		}
		if (last < line.length)
			segments.push({ text: unescapeSlackText(line.slice(last)) })
		return segments
	})
}

/** The message as the preview shows it, with sample values in place of the variables. */
export function previewSlackWelcomeMessage({
	template,
	name,
	channelName,
}: {
	template: string
	name: string
	channelName: string
}): SlackPreviewSegment[][] {
	return parseSlackMrkdwn(
		template
			.replace(/\{name\}/g, escapeSlackText(name))
			.replace(/\{channel\}/g, `<#CPREVIEW|${channelName}>`),
	)
}
